import { forwardRef, type InputHTMLAttributes, type ReactNode } from 'react';

/**
 * `<DateField>` — tokens-styled `<input type="date">` mirroring the
 * `<TextField>` chrome (32px height, `--radius` border, `--border` 1px,
 * `--surface` background, `--ring` focus). Built for turn 16.2c.2 as the
 * `/tasks/:id` inspector "Due" control; reusable anywhere a tokens-aware
 * native date control is needed.
 *
 * Value contract: `null` clears, otherwise an ISO `YYYY-MM-DD` string.
 */

export interface DateFieldProps
  extends Omit<
    InputHTMLAttributes<HTMLInputElement>,
    'value' | 'onChange' | 'type' | 'children'
  > {
  id: string;
  label: ReactNode;
  value: string | null;
  onChange: (next: string | null) => void;
  min?: string;
  max?: string;
  disabled?: boolean;
  helpText?: ReactNode;
  errorText?: ReactNode;
  /** Visually hide the label (kept in DOM for screen-readers). */
  hideLabel?: boolean;
  /** Borderless-until-hover/focus variant for inline-edit cells. */
  inline?: boolean;
  /** Always-visible bordered chrome for table inline-edit cells. */
  table?: boolean;
  testId?: string;
}

export const DateField = forwardRef<HTMLInputElement, DateFieldProps>(
  function DateField(
    {
      id,
      label,
      value,
      onChange,
      min,
      max,
      disabled,
      helpText,
      errorText,
      hideLabel,
      inline,
      table,
      testId,
      className,
      ...rest
    },
    ref
  ) {
    const helpId = helpText ? `${id}-help` : undefined;
    const errorId = errorText ? `${id}-error` : undefined;
    const describedBy =
      [helpId, errorId].filter(Boolean).join(' ') || undefined;
    return (
      <div
        className={[
          'pn-field',
          'pn-date-field',
          table ? 'pn-field-table' : null,
          inline ? 'pn-field-inline' : null,
          className
        ]
          .filter(Boolean)
          .join(' ')}
      >
        <label
          htmlFor={id}
          className={[
            'pn-field-label',
            hideLabel ? 'pn-field-label-hidden' : null
          ]
            .filter(Boolean)
            .join(' ')}
        >
          {label}
        </label>
        <input
          ref={ref}
          id={id}
          type="date"
          className="pn-field-input pn-date-field-input"
          value={value ?? ''}
          min={min}
          max={max}
          disabled={disabled}
          aria-describedby={describedBy}
          aria-invalid={errorText ? true : undefined}
          data-testid={testId}
          onChange={(e) => {
            const next = e.target.value;
            onChange(next ? next : null);
          }}
          {...rest}
        />
        {helpText ? (
          <span id={helpId} className="pn-field-help">
            {helpText}
          </span>
        ) : null}
        {errorText ? (
          <span id={errorId} className="pn-field-error" role="alert">
            {errorText}
          </span>
        ) : null}
      </div>
    );
  }
);
